import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import EdenBar from "@churchofjesuschrist/eden-bar";
import { MoreFormalH1 } from "@churchofjesuschrist/eden-headings";
import { Text1, Text4 } from "@churchofjesuschrist/eden-text";
import { white, spacing16, spacing32, fontWeightSemiBold } from "@churchofjesuschrist/eden-style-constants";
import Stack from "@churchofjesuschrist/eden-stack";
import { ReadWidth as EdenReadWidth } from "@churchofjesuschrist/eden-content-widths";
import { Narrow as EdenLightRays } from "@churchofjesuschrist/eden-light-rays";
import Parallax from "@churchofjesuschrist/eden-parallax";
import { mediaBreak480w, mediaBreak1600w } from "@churchofjesuschrist/eden-styled-helpers";
const AttachedHeroStyled = styled.div.withConfig({
  componentId: "sc-1hsu12o-0"
})(["display:flex;flex-direction:column;"]);
const ReadWidth = styled(EdenReadWidth).withConfig({
  componentId: "sc-1hsu12o-1"
})(["justify-content:center;padding:", ";z-index:2;width:100%;background:", ";"], spacing32, white);
const Text = styled.div.withConfig({
  componentId: "sc-1hsu12o-2"
})(["display:flex;flex-direction:column;align-items:center;text-align:center;"]);
const Body = styled(Text1).withConfig({
  componentId: "sc-1hsu12o-3"
})(["", ""], _ref => {
  let {
    inverted
  } = _ref;
  return inverted ? `font-weight: ${fontWeightSemiBold};` : "";
});
const Bar = styled(EdenBar).withConfig({
  componentId: "sc-1hsu12o-4"
})(["order:1;margin:", " auto;"], spacing16);
const HeroImageContainerStyled = styled.div.withConfig({
  componentId: "sc-1hsu12o-5"
})(["position:relative;width:100%;overflow:hidden;height:", "px;", " ", ""], 200, mediaBreak480w`
    height: ${350}px;
  `, mediaBreak1600w`
    height: ${500}px;
  `);
const StyledEdenLightRays = styled(EdenLightRays).withConfig({
  componentId: "sc-1hsu12o-6"
})(["position:absolute;bottom:-1px;"]);
const StyledParallax = styled(Parallax).withConfig({
  componentId: "sc-1hsu12o-7"
})(["margin:auto;width:100%;height:100%;& :where(img,picture){object-fit:cover;min-width:100%;height:100%;}"]);
const ActionContainer = styled.div.withConfig({
  componentId: "sc-1hsu12o-8"
})(["display:flex;justify-content:center;"]);

function AttachedHero(_ref2) {
  let {
    image,
    title,
    body,
    meta,
    callToAction,
    playButton,
    imageOverlay = "light-rays",
    ...rest
  } = _ref2;
  const titleOnly = title && !(body || meta || callToAction || playButton);
  return /*#__PURE__*/React.createElement(AttachedHeroStyled, rest, image && /*#__PURE__*/React.createElement(HeroImageContainerStyled, {
    className: "image-container"
  }, /*#__PURE__*/React.createElement(StyledParallax, {
    speed: "-2",
    className: "parallax"
  }, image), imageOverlay === "none" ? null : /*#__PURE__*/React.createElement(StyledEdenLightRays, {
    color: "white",
    origin: "bottom",
    className: "rays"
  }), playButton), /*#__PURE__*/React.createElement(ReadWidth, {
    image: image
  }, /*#__PURE__*/React.createElement(Text, {
    titleOnly: titleOnly
  }, titleOnly && /*#__PURE__*/React.createElement(Bar, {
    "data-testid": "bar",
    size: "jumbo"
  }), /*#__PURE__*/React.createElement(Stack, {
    gapSize: "32"
  }, /*#__PURE__*/React.createElement(Stack, null, title && /*#__PURE__*/React.createElement(MoreFormalH1, null, title), body && /*#__PURE__*/React.createElement(Body, null, body), meta && /*#__PURE__*/React.createElement(Text4, null, meta)), /*#__PURE__*/React.createElement(ActionContainer, null, callToAction)))));
}

AttachedHero.propTypes = {
  /** The main title. */
  title: PropTypes.string.isRequired,

  /** The background image. */
  image: PropTypes.node,

  /** Additional body text further explaining the page. */
  body: PropTypes.node,

  /** Additional meta information (such as a citation, etc.). */
  meta: PropTypes.string,

  /** A call-to-action link. Should be a [primary button](https://eden.churchofjesuschrist.org/?path=/story/base-eden-buttons--primary). */
  callToAction: PropTypes.node,

  /** A play button. Must be a single `<PlayButton>` element exported from this package. */
  playButton: PropTypes.element,

  /** Sets image overlay. */
  imageOverlay: PropTypes.oneOf(["light-rays", "none"])
};
export default AttachedHero;